import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import Header from "./Header";
import Footer from "./Footer";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import config from "../../src/config";

const UploadNotice = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const menuToggle = document.getElementById("sidebar-hide");
    const sidebar = document.querySelector(".pc-sidebar");
    if (!menuToggle) return;

    const toggleSidebar = () => {
      sidebar.classList.toggle("pc-sidebar-hide");
    };

    menuToggle.addEventListener("click", toggleSidebar);

    return () => {
      menuToggle.removeEventListener("click", toggleSidebar);
    };
  }, [location.pathname]);

  const [loading, setLoading] = useState(true);
  const [fileInputKey, setFileInputKey] = useState(Date.now());
  const [notices, setNotices] = useState([]);

  const token2 = localStorage.getItem("token2");

  const fetchData = async () => {
    try {
      const response = await fetch(
        `${config.API_BASE_URL}/auth/FetchAdminNoticeData`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        Swal.fire({
          icon: "warning",
          title: "Data not found.",
        });
        setNotices([]);
      } else {
        const result = await response.json();
        const studentData = result?.data?.notice_details ?? [];

        if (studentData.length === 0) {
          Swal.fire({
            icon: "warning",
            title: "Data Not Found",
          });
        }
        setNotices(studentData);
        console.log(result);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire("Error", "Something went wrong!", "error");
      setNotices([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("token2")) {
      navigate("/");
    }
    fetchData();
  }, [token2]);

  const [formData, setFormData] = useState({
    date: "",
    file: null,
    description: "",
    NOTICE_FILE_NAME: "",
    NOTICE_FILE_URL: "",
  });

  const [editId, setEditId] = useState(null);

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  const isVideoFile = (file, url) => {
    if (file) return file.type === "video/mp4";
    if (url) return url.toLowerCase().endsWith(".mp4");
    return false;
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "file") {
      const file = files[0];
      if (!file) return;

      const allowedTypes = ["application/pdf", "video/mp4"];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire("Error", "Only PDF and MP4 files are allowed", "error");
        setFileInputKey(Date.now());
        return;
      }

      const maxSize =
        file.type === "video/mp4"
          ? 50 * 1024 * 1024  // 50MB for video
          : 1 * 1024 * 1024;  // 1MB for PDF

      if (file.size > maxSize) {
        Swal.fire(
          "Warning",
          file.type === "video/mp4"
            ? "Video size must be less than 50MB"
            : "File size must be less than 1MB",
          "warning"
        );
        setFileInputKey(Date.now());
        return;
      }

      setFormData((prev) => ({ ...prev, file }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEdit = (item) => {
    try {
      setLoading(true);
      setFormData({
        date: convertToInputDate(item.SHOWING_DATE),
        file: null,
        description: item.NOTICE_DESCRIPTION,
        NOTICE_FILE_NAME: item.NOTICE_FILE_NAME,
        NOTICE_FILE_URL: item.NOTICE_FILE_URL,
      });
      setEditId(item.ID);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.date || !formData.description) {
      Swal.fire("Warning", "Please fill all required fields", "warning");
      return;
    }

    try {
      setLoading(true);

      let base64File = "";
      if (formData.file) {
        base64File = await convertToBase64(formData.file);
      }

      const payload = {
        id: editId ? String(editId) : "",
        description: formData.description,
        showinG_DATE: formatDate(formData.date),
        noticE_FILE: formData.file?.name || formData.NOTICE_FILE_NAME || "",
        image_Base64String: base64File || "",
      };

      const response = await fetch(
        `${config.API_BASE_URL}/auth/AdminInsertUpdateNoticeData`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token2}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.status !== 200) {
        Swal.fire(
          "Warning",
          result?.message || "Something went wrong",
          "warning"
        );
        return;
      }

      Swal.fire(
        "Success",
        editId ? "Notice updated successfully" : "Notice added successfully",
        "success"
      );

      setFormData({
        date: "",
        file: null,
        description: "",
        NOTICE_FILE_NAME: "",
        NOTICE_FILE_URL: "",
      });
      setEditId(null);
      setFileInputKey((k) => k + 1);
      fetchData();
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "API failed!", "warning");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const convertToInputDate = (date) => {
    const [day, month, year] = date.split("-");
    return `${year}-${month}-${day}`;
  };

  const getFileIcon = (fileName, fileUrl) => {
    const name = (fileName || fileUrl || "").toLowerCase();
    if (name.endsWith(".mp4")) return "fas fa-film";
    if (name.endsWith(".pdf")) return "fas fa-file-pdf";
    return "fas fa-file";
  };

  return (
    <div>
      <Navbar />
      <Header />
      <div className="pc-container">
        {loading && (
          <div className="custom-loader-container">
            <div className="custom-loader"></div>
          </div>
        )}
        <div className="pc-content">
          <div className="page-header">
            <div className="page-block">
              <div className="row align-items-center">
                <div className="col-md-12 breadcrumb">
                  <div className="page-header-title">
                    <h5 className="m-b-10">Upload Notice</h5>
                  </div>
                  <ul className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/Board-Dashboard">Home</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/upload-notice">Upload Notice</Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="notice-page">
            <div className="row g-4" style={{ padding: "0px" }}>
              {/* ─── FORM CARD ─── */}
              <div className="col-lg-5">
                <div className="card notice-card">
                  <div
                    className="card-header notice-card-header"
                    style={{ backgroundColor: "#188FFE" }}
                  >
                    <h5 className="mb-0 text-white">
                      {editId ? "Edit Notice" : "Upload Notice"}
                    </h5>
                  </div>

                  <div className="card-body">
                    <form className="notice-form" onSubmit={handleSubmit}>
                      <div className="row g-3">
                        {/* Date */}
                        <div className="col-md-6">
                          <label className="form-label">
                            Date{" "}
                            <span className="text-danger font-bold">* </span>
                          </label>
                          <input
                            type="date"
                            name="date"
                            className="form-control"
                            value={formData.date}
                            onChange={handleChange}
                          />
                        </div>

                        {/* File Upload */}
                        <div className="col-md-6">
                          <label className="form-label">
                            Notice File{" "}
                            <span className="text-danger font-bold">*</span>
                            <span className="text-muted ms-1">
                              (PDF / MP4 &mdash; PDF max 1MB, MP4 max 50MB)
                            </span>
                          </label>

                          <div className="file-box">
                            {/* Hidden Input */}
                            <input
                              type="file"
                              name="file"
                              accept="application/pdf,video/mp4"
                              onChange={handleChange}
                              key={fileInputKey}
                              id="fileUpload"
                              hidden
                            />

                            {/* File Display Area */}
                            <label
                              htmlFor="fileUpload"
                              className="file-display"
                            >
                              <i
                                className={`${getFileIcon(
                                  formData.file?.name,
                                  formData.NOTICE_FILE_NAME
                                )} me-2`}
                              ></i>
                              <span className="file-text">
                                {formData.file
                                  ? formData.file.name
                                  : formData.NOTICE_FILE_NAME || "Choose File"}
                              </span>
                            </label>

                            {/* Preview Button */}
                            {(formData.NOTICE_FILE_URL || formData.file) && (
                              <a
                                href={
                                  formData.file
                                    ? URL.createObjectURL(formData.file)
                                    : formData.NOTICE_FILE_URL
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="eye-btn"
                                title={
                                  isVideoFile(
                                    formData.file,
                                    formData.NOTICE_FILE_URL
                                  )
                                    ? "Play Video"
                                    : "View File"
                                }
                              >
                                <i
                                  className={
                                    isVideoFile(
                                      formData.file,
                                      formData.NOTICE_FILE_URL
                                    )
                                      ? "fas fa-play"
                                      : "fas fa-eye"
                                  }
                                ></i>
                              </a>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        <div className="col-12">
                          <label className="form-label">
                            Description{" "}
                            <span className="text-danger font-bold">* </span>
                          </label>
                          <textarea
                            name="description"
                            className="form-control"
                            maxLength={500}
                            rows="4"
                            value={formData.description}
                            onChange={handleChange}
                          />
                        </div>

                        {/* Buttons */}
                        <div className="col-12 d-flex justify-content-end gap-2">
                          <button
                            type="button"
                            className="btn btn-light"
                            style={{ backgroundColor: "gray", color: "#fff" }}
                            onClick={() => {
                              setFormData({
                                date: "",
                                file: null,
                                description: "",
                                NOTICE_FILE_NAME: "",
                                NOTICE_FILE_URL: "",
                              });
                              setEditId(null);
                              setFileInputKey((k) => k + 1);
                            }}
                          >
                            <i className="fas fa-times"></i> Cancel
                          </button>
                          <button type="submit" className="btn btn-primary">
                            <i className="fas fa-save"></i> Save
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              {/* ─── TABLE CARD ─── */}
              <div className="col-lg-7">
                <div className="card notice-card">
                  <div
                    className="card-header notice-card-header"
                    style={{ backgroundColor: "#188FFE" }}
                  >
                    <h5 className="mb-0 text-white">Notice List</h5>
                  </div>

                  <div className="card-body">
                    <div className="table-wrapper">
                      <table className="table table-bordered align-middle notice-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>File View</th>
                            <th>Description</th>
                            <th>Action</th>
                          </tr>
                        </thead>

                        <tbody>
                          {notices.length > 0 ? (
                            notices.map((item, index) => {
                              const isVideo = (item.NOTICE_FILE_URL || "")
                                .toLowerCase()
                                .endsWith(".mp4");
                              return (
                                <tr key={item.ID}>
                                  <td>{index + 1}</td>
                                  <td>{item.SHOWING_DATE}</td>
                                  <td>
                                    <a
                                      href={item.NOTICE_FILE_URL}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title={
                                        isVideo ? "Play Video" : "View PDF"
                                      }
                                    >
                                      <i
                                        className={`${
                                          isVideo
                                            ? "fas fa-film text-danger"
                                            : "fas fa-file-pdf text-danger"
                                        } me-1`}
                                      ></i>
                                      {item.NOTICE_FILE_NAME}
                                    </a>
                                  </td>
                                  <td>{item.NOTICE_DESCRIPTION}</td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleEdit(item)}
                                    >
                                      <i className="fas fa-edit"></i> Edit
                                    </button>
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="5" className="text-center">
                                No Data Available
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default UploadNotice;
